/**
 * Beheer (sessie 70, W5-3): alle beheerschermen op één plek. De lijst op
 * `/beheer` leest hieruit, en de oude adressen (`/admin/...`, `/oud/admin/...`,
 * `/gebruikers`, `/instellingen`) sturen via `beheerPadNaar` naar hetzelfde
 * scherm op zijn nieuwe plek.
 */

export interface BeheerScherm {
  /** Waar de regel heen gaat. */
  naar: string;
  woord: string;
  /** Alleen voor beheerders (rol admin), zoals de API erachter. */
  alleenBeheer: boolean;
}

export interface BeheerGroep {
  kop: string;
  schermen: BeheerScherm[];
}

const beheer = (naar: string, woord: string): BeheerScherm => ({
  naar,
  woord,
  alleenBeheer: true,
});
const iedereen = (naar: string, woord: string): BeheerScherm => ({
  naar,
  woord,
  alleenBeheer: false,
});

export const BEHEER_GROEPEN: BeheerGroep[] = [
  {
    kop: "Werkplek",
    schermen: [
      beheer("/beheer/instellingen", "Instellingen"),
      iedereen("/kennis?tab=kaarten", "Waarheidskaarten"),
    ],
  },
  {
    kop: "Mensen",
    schermen: [
      iedereen("/beheer/gebruikers", "Gebruikers en toegang"),
      iedereen("/beheer/afwezigheid", "Afwezigheid"),
      beheer("/beheer/medewerkers", "Medewerkers"),
      beheer("/beheer/hugo-sleutels", "Hugo-sleutels"),
    ],
  },
  {
    kop: "Mail en kanalen",
    schermen: [
      beheer("/beheer/mailboxen", "Mailboxen"),
      beheer("/beheer/sjablonen", "Sjablonen"),
      beheer("/beheer/whatsapp-templates", "WhatsApp-sjablonen"),
      beheer("/beheer/storingen", "Storingen"),
    ],
  },
  {
    kop: "Checkout en webshop",
    schermen: [
      beheer("/beheer/checkout", "Checkout"),
      beheer("/beheer/checkout-meting", "Checkout-meting"),
      beheer("/beheer/webshop-extras", "Webshop-extra's"),
      beheer("/beheer/zoeken", "Zoeken"),
      beheer("/beheer/recovery-mails", "Winkelwagenmails"),
      beheer("/beheer/optins", "Opt-ins"),
      beheer("/beheer/voorraadmeldingen", "Voorraadmeldingen"),
      beheer("/beheer/product-gaten", "Productgaten"),
    ],
  },
  {
    kop: "Kwaliteit",
    schermen: [
      beheer("/beheer/servicecijfers", "Servicecijfers"),
      beheer("/beheer/correctie-dashboard", "Correcties"),
      beheer("/beheer/kwaliteitsrun", "Kwaliteitsrun"),
      beheer("/beheer/sanne-toets", "Sanne-toets"),
    ],
  },
];

/** Oude `/admin/<naam>` die op een andere plek staat dan `/beheer/<naam>`. */
const ANDERS: Record<string, string> = {
  checkout: "/beheer/checkout-meting",
  "handtekening-instellingen": "/beheer/instellingen",
  automatisering: "/beheer/instellingen",
  knowledge: "/kennis",
  "kennis-gaten": "/kennis",
};

/** Oude `/admin/<naam>` die één op één `/beheer/<naam>` is. */
const ZELFDE = new Set([
  "medewerkers",
  "mailboxen",
  "sjablonen",
  "whatsapp-templates",
  "hugo-sleutels",
  "storingen",
  "servicecijfers",
  "optins",
  "recovery-mails",
  "voorraadmeldingen",
  "product-gaten",
  "webshop-extras",
  "zoeken",
  "kwaliteitsrun",
  "sanne-toets",
  "correctie-dashboard",
]);

/** Gesprekken hebben hun eigen doorstuur in `oudPadNaar` (naar Vandaag). */
const GESPREK = new Set(["chat", "whatsapp", "chat-archief", "whatsapp-archief"]);

const UUID = /^[0-9a-f-]{36}$/i;

/**
 * Het nieuwe adres voor een oud beheerpad, of `null` als het geen
 * beheerpad is (dan beslist `oudPadNaar`). De zoekstring gaat mee, zodat
 * een oude link met `?tab=` op hetzelfde tabblad landt.
 */
export function beheerPadNaar(pathname: string, search = ""): string | null {
  const pad = pathname.replace(/^\/oud(?=\/)/, "").replace(/\/+$/, "");
  if (pad === "/gebruikers" || pad.startsWith("/gebruikers/")) {
    return `/beheer/gebruikers${search}`;
  }
  if (pad === "/instellingen" || pad.startsWith("/instellingen/")) {
    return `/beheer/instellingen${search}`;
  }
  // Eindgrens: /administratie is geen beheerpad (zelfde regel als main.py).
  const admin = pad.match(/^\/admin(?:\/([^/]+)(?:\/([^/]+))?)?(?:\/|$)/);
  if (!admin) return null;
  const [, naam, rest] = admin;
  if (!naam) return "/beheer";
  if (GESPREK.has(naam)) return null;
  if (naam === "medewerkers" && rest) {
    // Alleen "nieuw" of een id; nooit een willekeurig pad doorgeven.
    if (rest === "nieuw" || UUID.test(rest)) return `/beheer/medewerkers/${rest}${search}`;
    return "/beheer/medewerkers";
  }
  if (naam === "knowledge" && rest && UUID.test(rest)) return `/kennis/bewerken/${rest}`;
  if (naam === "retouren") return rest === "nieuw" ? "/retouren/nieuw" : "/retouren";
  if (ANDERS[naam]) return `${ANDERS[naam]}${search}`;
  if (ZELFDE.has(naam)) return `/beheer/${naam}${search}`;
  return "/beheer";
}
